"use client";

import React from "react";
import { ShieldCheck, SlidersHorizontal as Sliders, CheckCircle2, ArrowRight, Download } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
export default function AstmHero() {
  const {
    lang
  } = useLanguage();
  const copy = {
    badgeStandard: {
      en: "Technical Standard",
      zh: "技术标准",
      es: "Norma técnica",
      de: "Technische Norm",
      fr: "Norme technique",
      pt: "Norma técnica"
    },
    badgeSpecification: {
      en: "Industrial Mesh Specification",
      zh: "工业丝网技术规范",
      es: "Especificación de malla industrial",
      de: "Industriegewebe-Spezifikation",
      fr: "Spécification de toile industrielle",
      pt: "Especificação de malha industrial"
    },
    title: {
      en: "ASTM E2016 Standard for Industrial Woven Wire Cloth",
      zh: "ASTM E2016 工业编织金属丝布标准解析",
      es: "Norma ASTM E2016 para tela de alambre tejida industrial",
      de: "ASTM E2016 Standard für industrielles Drahtgewebe",
      fr: "Norme ASTM E2016 relative aux toiles métalliques tissées industrielles",
      pt: "Norma ASTM E2016 para telas de arame tecidas industriais"
    },
    lead: {
      en: "The primary North American and international benchmark for wire cloth manufacture, dimensional tolerances, and quality inspection. MutualFilter engineers and manufactures every weave to meet or exceed ASTM E2016 guidelines.",
      zh: "北美及国际通用的金属丝布制造、尺寸公差与质量检验基准标准。MutualFilter 严格按照或超越 ASTM E2016 规范进行工程设计与精密织造。",
      es: "La referencia norteamericana e internacional para la fabricación de telas de alambre, tolerancias dimensionales e inspección de calidad. MutualFilter diseña y fabrica cada tejido conforme a la norma ASTM E2016.",
      de: "Der führende nordamerikanische und internationale Maßstab für Gewebeherstellung, Maßtoleranzen und Qualitätsprüfung. MutualFilter fertigt jedes Drahtgewebe nach den Richtlinien der ASTM E2016.",
      fr: "La référence nord-américaine et internationale pour la fabrication des toiles métalliques, les tolérances dimensionnelles et le contrôle qualité. MutualFilter conçoit et tisse chaque référence selon la norme ASTM E2016.",
      pt: "A principal referência norte-americana e internacional para fabricação de telas de arame, tolerâncias dimensionais e inspeção de qualidade. A MutualFilter projeta e produz cada trama em conformidade com a norma ASTM E2016."
    },
    downloadGuide: {
      en: "Download ASTM E2016 Guide",
      zh: "下载 ASTM E2016 指南",
      es: "Descargar guía ASTM E2016",
      de: "ASTM E2016 Leitfaden herunterladen",
      fr: "Télécharger le guide ASTM E2016",
      pt: "Baixar guia ASTM E2016"
    },
    cardOverlayBadge: {
      en: "ASTM E2016 / ISO 9044 Verified",
      zh: "ASTM E2016 / ISO 9044 规范验证",
      es: "Verificado ASTM E2016 / ISO 9044",
      de: "ASTM E2016 / ISO 9044 Verifiziert",
      fr: "Vérifié ASTM E2016 / ISO 9044",
      pt: "Verificado ASTM E2016 / ISO 9044"
    },
    imgAlt: {
      en: "Precision woven stainless steel wire mesh under calibrated optical inspection",
      zh: "经过校准光学检验的精密不锈钢编织丝网细节",
      es: "Malla de alambre de acero inoxidable tejida de precisión bajo inspección óptica calibrada",
      de: "Präzisions-Edelstahldrahtgewebe unter kalibrierter optischer Prüfung",
      fr: "Toile métallique tissée en acier inoxydable de précision sous contrôle optique étalonné",
      pt: "Malha de arame de aço inoxidável tecida com precisão sob inspeção óptica calibrada"
    },
    specHeading: {
      en: "Standard Manufacturing Parameters",
      zh: "标准制造技术参数",
      es: "Parámetros estándar de fabricación",
      de: "Standard-Fertigungsparameter",
      fr: "Paramètres standard de fabrication",
      pt: "Parâmetros padrão de fabricação"
    },
    specGrid: [{
      label: {
        en: "Standard Scope",
        zh: "标准适用范围",
        es: "Alcance de la norma",
        de: "Normenbereich",
        fr: "Champ d'application",
        pt: "Escopo da norma"
      },
      value: "ASTM E2016-20"
    }, {
      label: {
        en: "Weave Classes",
        zh: "编织类型",
        es: "Clases de tejido",
        de: "Gewebearten",
        fr: "Types de tissage",
        pt: "Tipos de trama"
      },
      value: {
        en: "Plain, Twill, Dutch",
        zh: "平纹、斜纹、密纹",
        es: "Liso, Sarga, Dutch",
        de: "Leinwand, Köper, Tresse",
        fr: "Uni, Sergé, Hollandais",
        pt: "Lisa, Sarja, Holandesa"
      }
    }, {
      label: {
        en: "Aperture Accuracy",
        zh: "网孔精度",
        es: "Precisión de abertura",
        de: "Maschengenauigkeit",
        fr: "Précision d'ouverture",
        pt: "Precisão de abertura"
      },
      value: "±2% to ±10%"
    }, {
      label: {
        en: "Quality Verification",
        zh: "质量检验",
        es: "Verificación de calidad",
        de: "Qualitätsprüfung",
        fr: "Contrôle qualité",
        pt: "Verificação da qualidade"
      },
      value: {
        en: "Optical QA & Mill Reports",
        zh: "光学质检与材质证明",
        es: "Control óptico e informes",
        de: "Optische QS & Zeugnisse",
        fr: "Contrôle optique et rapports",
        pt: "CQ óptico e relatórios"
      }
    }],
    formatsLabel: {
      en: "Rolls, slit coils, cut panels & stamped discs",
      zh: "原卷、分条卷带、定制切片与冲压滤圆片",
      es: "Rollos, bobinas cortadas, paneles y discos estampados",
      de: "Rollen, Spaltbänder, Zuschnitte & Ronden",
      fr: "Rouleaux, bobines refendues, panneaux et disques emboutis",
      pt: "Rolos, fitas cortadas, painéis e discos estampados"
    },
    oemLink: {
      en: "OEM / Custom Formats",
      zh: "供货形式与定制",
      es: "Formatos OEM / A medida",
      de: "OEM / Sonderformate",
      fr: "Formats OEM / Sur mesure",
      pt: "Formatos OEM / Personalizados"
    }
  };
  return <section id="astme2016hero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground isolate">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Main Content Column (7 cols on lg) */}
          <div className="lg:col-span-7 flex flex-col items-start justify-center">
            <Reveal>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 text-[13px] font-mono tracking-tight text-foreground">
                  <Sliders className="size-3.5 text-primary" />
                  {pick(lang, copy.badgeStandard)}
                </Badge>
                <Badge variant="outline" className="rounded-md border border-border bg-muted px-2.5 py-1 text-[13px] font-mono text-foreground">
                  <ShieldCheck className="mr-1 size-3 text-primary inline" />
                  {pick(lang, copy.badgeSpecification)}
                </Badge>
              </div>

              <h1 className="font-default text-balance mf-h1 text-foreground">
                {pick(lang, copy.title)}
              </h1>

              <p className="mf-lead text-muted-foreground mt-4 max-w-2xl">
                {pick(lang, copy.lead)}
              </p>

              {/* Quick action buttons */}
              <div className="flex flex-wrap items-center gap-4 mt-6">
                <Button asChild size="lg">
                  <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                    <span>{pick(lang, COMMON.requestQuote)}</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg">
                  <Link to="/DownloadsPage" className="inline-flex items-center gap-2">
                    <Download className="size-4" />
                    <span>{pick(lang, copy.downloadGuide)}</span>
                  </Link>
                </Button>
              </div>
            </Reveal>
          </div>

          {/* Technical Card Column (5 cols on lg) */}
          <div className="lg:col-span-5 w-full">
            <Reveal delay={120}>
              <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
                {/* Hero Product Visual with Overlay Badge */}
                <div className="mf-card-media mf-ratio-hero relative w-full overflow-hidden">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-woven-stainles-a1af9d.png" alt={pick(lang, copy.imgAlt)} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                  <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md text-[13px] font-mono text-foreground flex items-center gap-1.5">
                    <ShieldCheck className="size-3.5 text-primary" />
                    <span>{pick(lang, copy.cardOverlayBadge)}</span>
                  </div>
                </div>

                {/* Card Content Footer Row */}
                <CardContent className="p-4">
                  <div className="flex items-center justify-between text-[13px]">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <CheckCircle2 className="size-3.5 text-primary" />
                      <span>{pick(lang, copy.formatsLabel)}</span>
                    </div>
                    <Link to="/SlitCoilsAndStampedFilterDiscsPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                      <span>{pick(lang, copy.oemLink)}</span>
                      <ArrowRight className="size-3" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>

        {/* Spec Strip Below Grid */}
        <div className="mf-spec-strip">
          {copy.specGrid.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <div className="mf-spec-strip__label">
                {pick(lang, spec.label)}
              </div>
              <div className="mf-spec-strip__value font-mono">
                {typeof spec.value === "string" ? spec.value : pick(lang, spec.value)}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}